import { createGlobalStyle } from "styled-components";

/**
 * Global CSS for the Email Canvas — ported faithfully from builder-prototype.html.
 *
 * Class names are intentionally kept identical to the prototype so that
 * blockRenderers.tsx output matches the visual spec exactly.
 *
 * CSS variables --brand, --brand-d, --header-fg, --font are injected by Canvas.tsx
 * on the .email wrapper element.
 */
export const EmailCanvasGlobalStyle = createGlobalStyle`
	/* ---------- CANVAS HOST ---------- */
	.canvas-host {
		display: flex;
		justify-content: center;
		align-items: flex-start;
		padding: 26px 20px;
		background: #e7ebf1;
	}
	.canvas-host.dark {
		background: #0b1018;
	}

	/* ---------- EMAIL FRAME ---------- */
	.email {
		width: 600px;
		max-width: 100%;
		background: #fff;
		border-radius: 16px;
		overflow: hidden;
		box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
		font-family: var(--font, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif);
	}
	.blk {
		position: relative;
	}
	/* Drop indicator shown while dragging a block from the palette into the canvas */
	.palette-drop-line {
		height: 4px;
		background: var(--brand, #005e9e);
		border-radius: 4px;
		margin: 2px 14px;
		box-shadow: 0 0 0 2px rgba(0, 94, 158, 0.18);
	}
	/* Field highlighted while dragging a VARIABLE over it (shows where it will drop) */
	.email.editing .wl-drop-target {
		outline: 2px dashed var(--brand, #005e9e);
		outline-offset: 2px;
		border-radius: 4px;
		background: rgba(0, 94, 158, 0.07);
	}

	/* ---------- EMAIL VISUALS ---------- */
	.e-header {
		background: linear-gradient(135deg, var(--brand, #005e9e) 0%, var(--brand-d, #003d6b) 100%);
		color: var(--header-fg, #ffffff);
		text-align: center;
		padding: 28px 22px;
	}
	/* Mirror the sent email: logo as a 42px rounded badge on the brand header. */
	.e-logo {
		margin: 0 auto 9px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.e-logo img {
		display: block;
		width: 42px;
		height: 42px;
		border-radius: 50%;
		object-fit: contain;
		background: transparent;
	}
	.e-header h1 {
		margin: 0;
		font-size: 21px;
		font-weight: 700;
	}
	.e-header p {
		margin: 5px 0 0;
		font-size: 12px;
		opacity: 0.85;
	}
	.e-body {
		padding: 24px;
	}
	.e-chip {
		text-align: center;
		margin-bottom: 16px;
	}
	.e-chip span {
		background: #eaf3fb;
		color: var(--brand, #005e9e);
		font-size: 12px;
		font-weight: 600;
		padding: 7px 18px;
		border-radius: 999px;
		display: inline-block;
		border: 1px solid #bcd9ee;
	}
	.e-p {
		font-size: 14px;
		color: #334155;
		line-height: 1.55;
		margin: 0;
	}
	/* overflow wrap everywhere inside the email */
	.e-header h1,
	.e-header p,
	.e-body,
	.e-body *,
	.e-footer,
	.e-footer * {
		overflow-wrap: anywhere;
		word-break: break-word;
	}
	/* ---------- SUMMARY C2 (contract visual) ---------- */
	/* Replaces the old .e-summary/.e-row with the new label-left·value-right layout. */
	.e-summary-c2 {
		margin: 8px 0;
	}
	/* Section header row — bold title with brand tick */
	.e-sh {
		position: relative;
		font-size: 14.5px;
		font-weight: 700;
		color: #1a2940;
		margin: 18px 0 4px;
		padding-left: 12px;
	}
	/* Individual data rows */
	.e-row-c2 {
		display: flex;
		align-items: baseline;
		padding: 9px 12px;
		border-radius: 7px;
	}
	.e-row-c2 .lbl {
		flex: 0 0 148px;
		width: 148px;
		font-size: 13px;
		color: #6e7f93;
	}
	.e-row-c2 .val {
		flex: 1;
		font-size: 14px;
		font-weight: 600;
		color: #1a2940;
	}
	/* Pair row — right-side label + value */
	.e-row-pair-c2 .lbl2 {
		flex: 0 0 auto;
		font-size: 13px;
		color: #6e7f93;
		margin: 0 8px 0 18px;
		white-space: nowrap;
	}
	.e-row-pair-c2 .val2 {
		flex: 0 0 auto;
		font-size: 14px;
		font-weight: 600;
		color: #1a2940;
		white-space: nowrap;
	}
	/* Emphasis (Total) row */
	.e-row-em-c2 {
		background: #eef6fc !important;
	}
	.e-row-em-c2 .lbl {
		color: #1a2940 !important;
		font-weight: 700 !important;
	}
	.e-row-em-c2 .val {
		font-size: 17px !important;
		font-weight: 800 !important;
		color: var(--brand, #005e9e) !important;
	}
	/* Keep legacy .e-summary/.e-row for any old blocks that may still exist */
	.e-summary {
		border: 1px solid #e3e8ef;
		border-radius: 12px;
		overflow: hidden;
		margin: 8px 0;
	}
	.e-row {
		padding: 13px 16px;
		border-bottom: 1px solid #eef2f7;
		position: relative;
	}
	.e-row:last-child {
		border-bottom: none;
	}
	.e-row .lbl {
		font-size: 11px;
		color: #64748b;
	}
	.e-row .val {
		font-size: 15px;
		color: #1e293b;
		font-weight: 600;
		margin-top: 3px;
	}
	.e-row .val.emph {
		font-size: 22px;
		color: var(--brand, #005e9e);
		font-weight: 800;
	}
	.e-row.inline {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: 14px;
	}
	.e-row.inline .lbl {
		flex: 0 0 auto;
	}
	.e-row.inline .val {
		margin-top: 0;
		text-align: right;
	}
	/* ---------- NOTES (contract visual) ---------- */
	.e-notes {
		border-radius: 8px;
		padding: 12px 16px;
		margin: 10px 0;
		background: #fafbfc;
		border: 1px solid #e8edf3;
		font-size: 13px;
		color: #51627a;
		line-height: 1.65;
	}
	.e-notes.info {
		background: #fafbfc;
		border-color: #e8edf3;
	}
	.e-notes.warning {
		background: #fef6e7;
		border-color: #f5e4bb;
	}
	.e-notes.neutral {
		background: #fafbfc;
		border-color: #e8edf3;
	}
	.e-notes h4 {
		margin: 0 0 7px;
		font-size: 13px;
		font-weight: 700;
		color: #1a2940;
		display: inline;
	}
	.e-notes h4::after {
		content: " ";
	}
	.e-notes.warning h4 {
		color: #a16207;
	}
	.e-notes ul {
		margin: 6px 0 0;
		padding-left: 18px;
	}
	.e-notes li {
		font-size: 13px;
		color: #51627a;
		margin: 5px 0;
		line-height: 1.65;
	}
	.e-noterow {
		display: flex;
		gap: 12px;
		font-size: 13px;
		margin: 7px 0;
		position: relative;
	}
	.e-noterow b {
		flex: 0 0 150px;
		color: #1f2937;
	}
	.e-noterow span {
		color: #475569;
	}
	.e-footer {
		border-top: 1px solid #eef2f7;
		padding: 20px 24px;
		color: #64748b;
	}
	.e-footer .t {
		font-size: 13px;
		font-weight: 700;
		color: #334155;
	}
	.e-footer .l {
		font-size: 12px;
		margin-top: 4px;
	}
	.e-footer .note {
		font-size: 11px;
		font-style: italic;
		margin-top: 9px;
		color: #94a3b8;
	}
	a.var {
		color: var(--brand, #005e9e);
		text-decoration: none;
	}

	/* ---------- HEADER EDITABLE PLACEHOLDERS ---------- */
	/* When an editable header field (eyebrow / subtitle) is empty, show a subtle
	   dimmed placeholder so the user can see where to click/drop. Only visible in
	   edit mode (".editing") so the preview never shows placeholder text. */
	.email.editing [data-placeholder]:empty::before {
		content: attr(data-placeholder);
		opacity: 0.4;
		pointer-events: none;
		font-style: italic;
	}

	/* ---------- VAR CHIP ---------- */
	.var-chip {
		display: inline-block;
		background: #eef5fb;
		color: var(--brand, #005e9e);
		border: 1px solid #cfe3f2;
		border-radius: 5px;
		padding: 0 5px;
		font-size: 0.92em;
		font-weight: 600;
		white-space: nowrap;
		user-select: none;
		line-height: 1.5;
	}
	.var-chip.sample {
		background: rgba(0, 94, 158, 0.08);
		border: none;
		border-radius: 4px;
		color: inherit;
		font-weight: inherit;
		padding: 0 2px;
	}
	.email:not(.editing) .var-chip.sample {
		background: transparent;
		padding: 0;
	}

	/* ---------- DARK MODE ---------- */
	.email.dark {
		background: #161d2b;
	}
	.email.dark .e-p,
	.email.dark .e-row .lbl,
	.email.dark .e-row-c2 .lbl,
	.email.dark .e-row-pair-c2 .lbl2 {
		color: #94a3b8;
	}
	.email.dark .e-row .val,
	.email.dark .e-row-c2 .val,
	.email.dark .e-row-pair-c2 .val2,
	.email.dark .e-sh {
		color: #e5e7eb;
	}
	.email.dark .e-summary,
	.email.dark .e-row {
		border-color: #2a3446;
	}
	.email.dark .e-row-em-c2 {
		background: #0f2133 !important;
	}
	.email.dark .e-row-em-c2 .lbl {
		color: #e2e8f0 !important;
	}
	.email.dark .e-row-em-c2 .val {
		color: #7db8e6 !important;
	}
	.email.dark .e-notes {
		background: #1a2535;
		border-color: #2a3446;
		color: #94a3b8;
	}
	.email.dark .e-notes.info {
		background: #10243a;
		border-color: #1d3a52;
	}
	.email.dark .e-notes.warning {
		background: #3a2f10;
		border-color: #5a4510;
	}
	.email.dark .e-notes.neutral {
		background: #1f2735;
		border-color: #2a3446;
	}
	.email.dark .e-notes li,
	.email.dark .e-noterow span {
		color: #cbd5e1;
	}
	.email.dark .e-notes h4 {
		color: #e2e8f0;
	}
	.email.dark .e-footer {
		border-color: #2a3446;
		color: #94a3b8;
	}
	.email.dark .e-noterow b {
		color: #e2e8f0;
	}
	.email.dark .e-notes.info h4,
	.email.dark .e-notes.warning h4 {
		color: #7db8e6;
	}
	.email.dark .e-notes.warning h4 {
		color: #fbbf24;
	}
	.email.dark .e-row .val.emph,
	.email.dark a.var,
	.email.dark .var-chip {
		color: #7db8e6;
	}
	.email.dark .e-footer .t {
		color: #e2e8f0;
	}
	.email.dark .e-chip span {
		background: #10243a;
		border-color: #1d3a52;
		color: #7db8e6;
	}
	.email.dark .var-chip {
		background: #10243a;
		border-color: #1d3a52;
	}
	.email.dark .var-chip.sample {
		background: rgba(125, 184, 230, 0.12);
		color: inherit;
	}
	.email.dark:not(.editing) .var-chip.sample {
		background: transparent;
	}
`;
